import React from 'react'

interface PrivacyPolicyProps {
  onClose?: () => void
  isModal?: boolean
}

export default function PrivacyPolicy({ onClose, isModal = false }: PrivacyPolicyProps) {
  const containerClass = isModal ? 'privacy-modal' : 'privacy-page'

  return (
    <div className={containerClass} style={{ 
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      backgroundColor: '#1a202c',
      color: '#e2e8f0',
      lineHeight: '1.6',
      padding: '20px',
      minHeight: '100vh'
    }}>
      {isModal && (
        <div className="modal-overlay" onClick={onClose} style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          zIndex: 1000
        }} />
      )}
      
      <div className={`privacy-container ${isModal ? 'modal-content' : ''}`} style={{
        maxWidth: '800px',
        margin: '0 auto',
        backgroundColor: '#2d3748',
        padding: '2rem',
        borderRadius: '12px',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4)',
        border: '1px solid #4a5568',
        position: isModal ? 'relative' : 'static',
        zIndex: isModal ? 1001 : 'auto'
      }}>
        <div className="privacy-header" style={{ marginBottom: '2rem' }}>
          <h1 style={{
            color: '#4cc380',
            fontSize: '2rem',
            fontWeight: 'bold',
            marginBottom: '1rem',
            textAlign: 'center'
          }}>Privacy Policy for Unbiased Kratom</h1>
          {isModal && (
            <button onClick={onClose} style={{
              position: 'absolute',
              top: '1rem',
              right: '1rem',
              background: 'none',
              border: 'none',
              color: '#e2e8f0',
              fontSize: '1.5rem',
              cursor: 'pointer'
            }}>
              ×
            </button>
          )}
          <div style={{
            textAlign: 'center',
            color: '#a0aec0',
            marginBottom: '2rem',
            fontStyle: 'italic'
          }}>Last Updated: July 10, 2025</div>
        </div>
        
        <div className="privacy-content">
          <p style={{ marginBottom: '1rem' }}>
            Unbiased Kratom ("we," "us," or "our") operates the website unbiasedkratom.com (the "Website"). We are committed to protecting your privacy and ensuring transparency about how we collect, use, and share your personal information, including photos and other data you provide. This Privacy Policy explains our practices in compliance with applicable U.S. federal and California state laws, including the California Consumer Privacy Act (CCPA) as amended by the California Privacy Rights Act (CPRA). This policy is precautionary and designed to address the future development of the Website.
          </p>

          <h2 style={{
            color: '#4cc380',
            fontSize: '1.3rem',
            marginTop: '2rem',
            marginBottom: '1rem'
          }}>1. Information We Collect</h2>
          <p style={{ marginBottom: '1rem' }}>We collect the following types of information:</p>
          
          <h3 style={{
            color: '#68d391',
            fontSize: '1.1rem',
            marginTop: '1.5rem',
            marginBottom: '0.5rem'
          }}>a. Personal Information</h3>
          <p style={{ marginBottom: '1rem' }}>Personal information is data that can be used to identify you, such as:</p>
          <ul style={{ marginLeft: '1.5rem', marginBottom: '1rem' }}>
            <li style={{ marginBottom: '0.5rem' }}>Name, email address, and contact details you provide when submitting content, registering, or contacting us.</li>
            <li style={{ marginBottom: '0.5rem' }}>User-generated content, including photos, reviews, comments, or testimonials you submit to the Website.</li>
            <li style={{ marginBottom: '0.5rem' }}>Account information, if you create an account, such as username and password.</li>
            <li style={{ marginBottom: '0.5rem' }}>Payment information, if applicable, processed securely through third-party payment processors (we do not store payment details).</li>
            <li style={{ marginBottom: '0.5rem' }}>Other information you voluntarily provide, such as feedback or survey responses.</li>
          </ul>

          <h3 style={{
            color: '#68d391',
            fontSize: '1.1rem',
            marginTop: '1.5rem',
            marginBottom: '0.5rem'
          }}>b. Non-Personal Information</h3>
          <p style={{ marginBottom: '1rem' }}>We collect non-identifying information, such as:</p>
          <ul style={{ marginLeft: '1.5rem', marginBottom: '1rem' }}>
            <li style={{ marginBottom: '0.5rem' }}><strong style={{ color: '#fff' }}>Usage data:</strong> Information about how you interact with the Website, including IP address, browser type, device information, pages visited, and time spent on the Website.</li>
            <li style={{ marginBottom: '0.5rem' }}><strong style={{ color: '#fff' }}>Cookies and tracking technologies:</strong> We use cookies, web beacons, and similar technologies to enhance your experience, analyze usage, and deliver personalized content. You can manage cookie preferences through your browser settings.</li>
          </ul>

          <h3 style={{
            color: '#68d391',
            fontSize: '1.1rem',
            marginTop: '1.5rem',
            marginBottom: '0.5rem'
          }}>c. Information from Third Parties</h3>
          <p style={{ marginBottom: '1rem' }}>We may receive information from third-party services (e.g., social media platforms) if you choose to link your account or share content through those services.</p>

          <h2 style={{
            color: '#4cc380',
            fontSize: '1.3rem',
            marginTop: '2rem',
            marginBottom: '1rem'
          }}>2. Taper Tracker Pro App Data</h2>
          <p style={{ marginBottom: '1rem' }}>Data and photos collected through the Taper Tracker Pro app, if applicable, will never be shared with third parties, except as required by law. This includes any personal information, usage data, or user-generated content submitted via the app. This policy ensures the confidentiality of all app-related data.</p>

          <h2 style={{
            color: '#4cc380',
            fontSize: '1.3rem',
            marginTop: '2rem',
            marginBottom: '1rem'
          }}>3. How We Use Your Information</h2>
          <p style={{ marginBottom: '1rem' }}>We use your information for the following purposes:</p>
          <ul style={{ marginLeft: '1.5rem', marginBottom: '1rem' }}>
            <li style={{ marginBottom: '0.5rem' }}><strong style={{ color: '#fff' }}>To operate and improve the Website:</strong> Displaying user photos, reviews, and content to provide unbiased information about kratom.</li>
            <li style={{ marginBottom: '0.5rem' }}><strong style={{ color: '#fff' }}>To communicate with you:</strong> Responding to inquiries, sending newsletters (if you opt in), or providing updates about the Website.</li>
            <li style={{ marginBottom: '0.5rem' }}><strong style={{ color: '#fff' }}>To personalize your experience:</strong> Tailoring content or recommendations based on your preferences and interactions.</li>
            <li style={{ marginBottom: '0.5rem' }}><strong style={{ color: '#fff' }}>For analytics and research:</strong> Understanding user behavior to improve our services and content.</li>
            <li style={{ marginBottom: '0.5rem' }}><strong style={{ color: '#fff' }}>For legal and safety purposes:</strong> Complying with legal obligations, protecting against fraud, and ensuring the security of the Website.</li>
          </ul>

          <h2 style={{
            color: '#4cc380',
            fontSize: '1.3rem',
            marginTop: '2rem',
            marginBottom: '1rem'
          }}>4. How We Share Your Information</h2>
          <p style={{ marginBottom: '1rem' }}>We may share your information in the following circumstances:</p>
          <ul style={{ marginLeft: '1.5rem', marginBottom: '1rem' }}>
            <li style={{ marginBottom: '0.5rem' }}><strong style={{ color: '#fff' }}>With your consent:</strong> When you submit photos, reviews, or other content to the Website, you consent to their display on the Website, where they may be publicly visible.</li>
            <li style={{ marginBottom: '0.5rem' }}><strong style={{ color: '#fff' }}>With service providers:</strong> We use third-party vendors (e.g., hosting providers, analytics tools, or payment processors) to operate the Website. These providers are contractually obligated to protect your information and use it only for the services they provide to us.</li>
            <li style={{ marginBottom: '0.5rem' }}><strong style={{ color: '#fff' }}>For legal reasons:</strong> We may disclose information to comply with applicable laws, regulations, or legal processes, or to protect our rights, property, or safety.</li>
            <li style={{ marginBottom: '0.5rem' }}><strong style={{ color: '#fff' }}>Business transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred to a successor entity, subject to this Privacy Policy.</li>
          </ul>
          <p style={{ marginBottom: '1rem' }}>We do not sell your personal information to third parties for monetary compensation. However, under the CCPA/CPRA, certain data sharing for targeted advertising may be considered a "sale" or "sharing." See Section 7 for your rights regarding this. As noted in Section 2, Taper Tracker Pro app data and photos will never be shared, except as required by law.</p>

          <h2 style={{
            color: '#4cc380',
            fontSize: '1.3rem',
            marginTop: '2rem',
            marginBottom: '1rem'
          }}>5. Your Choices</h2>
          <ul style={{ marginLeft: '1.5rem', marginBottom: '1rem' }}>
            <li style={{ marginBottom: '0.5rem' }}><strong style={{ color: '#fff' }}>Opting out of communications:</strong> You may opt out of marketing emails by clicking the "unsubscribe" link in our emails or contacting us at unbiasedkratom@gmail.com.</li>
            <li style={{ marginBottom: '0.5rem' }}><strong style={{ color: '#fff' }}>Cookies:</strong> You can disable cookies through your browser settings, though this may affect Website functionality.</li>
            <li style={{ marginBottom: '0.5rem' }}><strong style={{ color: '#fff' }}>Content removal:</strong> You may request removal of your photos or other content by contacting us at unbiasedkratom@gmail.com. We will process such requests in accordance with applicable laws.</li>
          </ul>

          <h2 style={{
            color: '#4cc380',
            fontSize: '1.3rem',
            marginTop: '2rem',
            marginBottom: '1rem'
          }}>6. Your California Privacy Rights</h2>
          <p style={{ marginBottom: '1rem' }}>If you are a California resident, the CCPA/CPRA grants you specific rights regarding your personal information:</p>
          
          <h3 style={{
            color: '#68d391',
            fontSize: '1.1rem',
            marginTop: '1.5rem',
            marginBottom: '0.5rem'
          }}>a. Right to Know</h3>
          <p style={{ marginBottom: '1rem' }}>You have the right to request, up to twice in a 12-month period, details about the personal information we collect, use, disclose, or sell/share, including categories of information, sources, purposes, and third parties with whom we share it.</p>
          
          <h3 style={{
            color: '#68d391',
            fontSize: '1.1rem',
            marginTop: '1.5rem',
            marginBottom: '0.5rem'
          }}>b. Right to Delete</h3>
          <p style={{ marginBottom: '1rem' }}>You may request the deletion of your personal information, subject to certain exceptions (e.g., where we need the information to comply with legal obligations).</p>
          
          <h3 style={{
            color: '#68d391',
            fontSize: '1.1rem',
            marginTop: '1.5rem',
            marginBottom: '0.5rem'
          }}>c. Right to Correct</h3>
          <p style={{ marginBottom: '1rem' }}>You may request correction of inaccurate personal information we hold about you.</p>
          
          <h3 style={{
            color: '#68d391',
            fontSize: '1.1rem',
            marginTop: '1.5rem',
            marginBottom: '0.5rem'
          }}>d. Right to Opt Out of Sale/Sharing</h3>
          <p style={{ marginBottom: '1rem' }}>You may opt out of the "sale" or "sharing" of your personal information for targeted advertising. To exercise this right, contact us at unbiasedkratom@gmail.com or use our "Do Not Sell or Share My Personal Information" link [insert link, if applicable].</p>
          
          <h3 style={{
            color: '#68d391',
            fontSize: '1.1rem',
            marginTop: '1.5rem',
            marginBottom: '0.5rem'
          }}>e. Right to Limit Use of Sensitive Personal Information</h3>
          <p style={{ marginBottom: '1rem' }}>We do not use sensitive personal information (e.g., precise geolocation, health data) for purposes beyond what is necessary to provide our services.</p>
          
          <h3 style={{
            color: '#68d391',
            fontSize: '1.1rem',
            marginTop: '1.5rem',
            marginBottom: '0.5rem'
          }}>f. Right to Non-Discrimination</h3>
          <p style={{ marginBottom: '1rem' }}>We will not discriminate against you for exercising your privacy rights, such as by denying services or charging different prices.</p>
          
          <p style={{ marginBottom: '1rem' }}>To exercise these rights, contact us at unbiasedkratom@gmail.com. We will verify your identity (e.g., by confirming your email or account details) before processing your request. You may also designate an authorized agent to submit requests on your behalf, provided they provide proof of authorization.</p>

          <h2 style={{
            color: '#4cc380',
            fontSize: '1.3rem',
            marginTop: '2rem',
            marginBottom: '1rem'
          }}>7. Data Retention</h2>
          <p style={{ marginBottom: '1rem' }}>We retain personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, comply with legal obligations, or resolve disputes. User-submitted content (e.g., photos, reviews) may remain on the Website until you request removal or we determine it is no longer relevant. Taper Tracker Pro app data will be retained only as necessary for app functionality and will not be shared, as stated in Section 2.</p>

          <h2 style={{
            color: '#4cc380',
            fontSize: '1.3rem',
            marginTop: '2rem',
            marginBottom: '1rem'
          }}>8. Security</h2>
          <p style={{ marginBottom: '1rem' }}>We implement reasonable technical and organizational measures to protect your personal information from unauthorized access, loss, or misuse. However, no system is completely secure, and we cannot guarantee absolute security.</p>

          <h2 style={{
            color: '#4cc380',
            fontSize: '1.3rem',
            marginTop: '2rem',
            marginBottom: '1rem'
          }}>9. Children's Privacy</h2>
          <p style={{ marginBottom: '1rem' }}>The Website is not intended for individuals under 21. We do not knowingly collect personal information from individuals under 21. If we learn that we have collected such information, we will delete it. Contact us at unbiasedkratom@gmail.com if you believe we have inadvertently collected information from someone under 21.</p>

          <h2 style={{
            color: '#4cc380',
            fontSize: '1.3rem',
            marginTop: '2rem',
            marginBottom: '1rem'
          }}>10. Third-Party Links</h2>
          <p style={{ marginBottom: '1rem' }}>The Website may contain links to third-party websites or services. We are not responsible for the privacy practices of these third parties. We encourage you to review their privacy policies.</p>

          <h2 style={{
            color: '#4cc380',
            fontSize: '1.3rem',
            marginTop: '2rem',
            marginBottom: '1rem'
          }}>11. International Users</h2>
          <p style={{ marginBottom: '1rem' }}>The Website is operated in the United States and governed by U.S. law. If you access the Website from outside the U.S., your information may be transferred to and processed in the U.S., where privacy laws may differ.</p>

          <h2 style={{
            color: '#4cc380',
            fontSize: '1.3rem',
            marginTop: '2rem',
            marginBottom: '1rem'
          }}>12. Changes to This Privacy Policy</h2>
          <p style={{ marginBottom: '1rem' }}>We may update this Privacy Policy to reflect changes in our practices or legal requirements. We will notify you of material changes by posting the updated policy on the Website or, where required, by direct communication. The "Last Updated" date at the top indicates when the policy was last revised.</p>

          <h2 style={{
            color: '#4cc380',
            fontSize: '1.3rem',
            marginTop: '2rem',
            marginBottom: '1rem'
          }}>13. Contact Us</h2>
          <p style={{ marginBottom: '1rem' }}>If you have questions, concerns, or requests regarding this Privacy Policy or our privacy practices, please contact us at:</p>
          <div style={{
            backgroundColor: '#1a202c',
            padding: '1rem',
            borderRadius: '8px',
            marginTop: '1rem',
            borderLeft: '4px solid #4cc380'
          }}>
            <strong style={{ color: '#fff' }}>Unbiased Kratom</strong><br />
            unbiasedkratom@gmail.com
          </div>
          <p style={{ marginBottom: '1rem' }}>For California residents exercising CCPA/CPRA rights, we will respond to verified requests within the timeframes required by law.</p>
        </div>
      </div>
    </div>
  )
}